/**
 * "Is THIS page load the return leg of an OAuth redirect?" — answered without
 * requiring our own `sessionStorage` redirect marker to have survived.
 *
 * WHY THIS EXISTS (THE-580). Both lost-redirect affordances — the
 * `auth_redirect_lost` telemetry and the `RedirectFailedToast` — were gated on
 * `markerPresent`, i.e. on `thea:authRedirectStarted` still being in
 * `sessionStorage` when we get back. That marker is the weakest link in the
 * chain it is guarding:
 *   - `v4Auth.writeRedirectMarker` SWALLOWS its own failure (Safari private
 *     mode / quota), so the marker can be absent on a redirect we initiated
 *     ourselves and nothing anywhere records that;
 *   - iOS evicts `sessionStorage` for backgrounded tabs under memory pressure —
 *     which is exactly the tab state a provider round-trip creates;
 *   - a return that lands in a NEW tab gets a fresh, empty `sessionStorage`.
 * In every one of those the sign-in failed AND the failure was invisible: no
 * event, no toast, nothing on screen. That is the silence in the 2026-09-25
 * feedback session.
 *
 * So: treat the marker as ONE signal among several rather than the gate.
 *
 * WHAT WE DELIBERATELY DO NOT USE. `getRedirectResult` resolving `no-result` is
 * NOT a signal — it resolves that way on every ordinary cold load, so keying off
 * it alone would tell a first-time visitor their sign-in failed. Every signal
 * below has to be positive evidence that a provider round-trip actually
 * happened.
 *
 * RESIDUAL BLIND SPOT, stated honestly: a return that lands in a new tab with
 * no referrer AND no surviving `sessionStorage` trips none of these. Nothing
 * observable client-side distinguishes that load from a cold visit. The modal's
 * own in-session attempt state (`V4SignInModal`) is the layer that covers the
 * user who comes back and taps again — see that component.
 */

import { BOOT_AUTH_STORAGE_KEYS } from '../../firebaseConfig';

/**
 * Which piece of evidence said "this is a redirect return". Carried on the
 * telemetry so we can see, in the warehouse, how often the marker was the ONLY
 * thing that caught it (and how often it was absent on a real return — the
 * number this whole module exists to measure).
 */
export type RedirectReturnSignal = 'marker' | 'sdk-record' | 'referrer';

export interface RedirectReturnEvidence {
  /** True when at least one signal fired. */
  isReturn: boolean;
  signals: RedirectReturnSignal[];
}

/**
 * The SDK's own redirect bookkeeping, snapshotted at module load in
 * `firebaseConfig` BEFORE auth init consumes it. Distinct from our marker
 * because the two are written by different code on different failure budgets:
 * ours is best-effort and silently skipped on a storage throw, the SDK's is
 * load-bearing for `getRedirectResult` itself. Present ⇒ a redirect was in
 * flight when this document loaded.
 */
function sdkRecordSignal(): boolean {
  try {
    return BOOT_AUTH_STORAGE_KEYS.some(
      (k) =>
        k.startsWith('firebase:pendingRedirect') ||
        k.startsWith('firebase:redirectUser'),
    );
  } catch {
    return false;
  }
}

/**
 * The one signal that survives `sessionStorage` eviction entirely: where this
 * document was navigated FROM.
 *
 * Two shapes both count:
 *  - our OWN `/__/auth/handler` — with the same-origin `authDomain` configured
 *    in `.env`, the last hop of the round-trip is a same-origin redirect off
 *    the Firebase auth handler, and same-origin referrers are not trimmed by
 *    ITP or by the default referrer policy;
 *  - the provider itself — covers the default `*.firebaseapp.com` authDomain
 *    and any hop that lands us back directly. Cross-site referrers are trimmed
 *    to the origin by default, which is all we need.
 *
 * ⚠ THE EMAIL ACTION PAGE MUST NOT MATCH EITHER RULE (THE-683). Firebase serves
 * the EMAIL ACTION page — verify-email, password reset, change-email — from
 * `/__/auth/action`, and the Continue button on it navigates to
 * `https://givethea.com/`. `getRedirectResult` then correctly resolves
 * `no-result` (nothing was ever pending), so if this function says "return leg"
 * the chokepoint in `AuthGateContext` tells a user whose sign-up worked fine
 * that "Sign-in didn't finish" — and fires `auth_redirect_lost`, inflating the
 * very metric this module exists to measure. OAuth never touches
 * `/__/auth/action`, which is why only the email+password cohort reported it.
 *
 * That page defeats BOTH rules, and it took two passes to get both:
 *
 *  1. THE PATH IS `/__/auth/handler`, NOT THE `/__/auth/` PREFIX. The prefix also
 *     covered `/__/auth/action`. `/__/auth/iframe` is likewise not a navigation
 *     source. Naming the handler exactly — rather than the directory it sits in —
 *     is what keeps this signal to "a round-trip actually happened."
 *
 *  2. `firebaseapp.com` IS NOT A PROVIDER HOST HERE. Which host sends the email
 *     action page is a SERVER-side Identity Platform setting, independent of the
 *     client `authDomain` above. For this project
 *     (`admin/v2/projects/thea-643b1/config`, read 2026-09-29):
 *
 *       notification.sendEmail.method      = "DEFAULT"
 *       notification.sendEmail.callbackUri = "https://thea-643b1.firebaseapp.com/__/auth/action"
 *
 *     With `handleCodeInApp: false` (`emailVerification.ts`) every such link
 *     lands there, NOT on `givethea.com`. So the Continue hop is CROSS-ORIGIN,
 *     the referrer is trimmed to the bare origin `https://thea-643b1.firebaseapp.com/`,
 *     there is no path left for rule 1 to match — and a `firebaseapp.com` entry
 *     in the host list below caught it regardless. Measured on the live prod
 *     bundle: that exact referrer raised the toast, while a same-build cold-load
 *     control did not.
 *
 *     Dropping it costs nothing, because `resolveAuthDomain` never yields a
 *     `firebaseapp.com` host: it returns the page's own origin for the two real
 *     production hosts and otherwise the build-time fallback, which the live prod
 *     bundle inlines as `"givethea.com"`. Our OAuth handler is therefore always
 *     reached at `/__/auth/handler` on a `givethea.com` host and is covered by
 *     rule 1. A genuine provider round-trip never presents a `firebaseapp.com`
 *     referrer here — only the email action page does.
 *
 *     If `authDomain` is ever repointed back to the default, the handler and the
 *     action page would SHARE that host and a bare-origin referrer could no
 *     longer tell them apart; the guard test in `__tests__/redirectReturn.test.ts`
 *     fails on that change and says so.
 *
 * Host matching is anchored (`===` or a dot-prefixed suffix) so an attacker
 * origin like `accounts.google.com.evil.test` cannot spoof it. Not a security
 * boundary — this only decides whether to show "sign-in didn't finish" — but a
 * substring match here would also false-positive on innocent lookalikes.
 */
const HANDLER_PATH = '/__/auth/handler';

/**
 * Only the identity providers themselves. `firebaseapp.com` is DELIBERATELY not
 * here — see the note above `referrerSignal`.
 */
const PROVIDER_REFERRER_HOSTS = ['accounts.google.com', 'appleid.apple.com'];

function hostMatches(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

function referrerSignal(): boolean {
  try {
    if (typeof document === 'undefined' || !document.referrer) return false;
    const url = new URL(document.referrer);
    if (url.pathname === HANDLER_PATH) return true;
    // A cross-origin provider referrer is trimmed to the bare origin, so there
    // is no path to inspect — the host is the whole signal. Which is exactly why
    // this list holds ONLY the providers: the email action page also arrives as a
    // bare cross-origin origin, and anything broader swallows it.
    return PROVIDER_REFERRER_HOSTS.some((h) => hostMatches(url.host, h));
  } catch {
    // An unparseable referrer is not evidence of anything.
    return false;
  }
}

/**
 * Collect the evidence. `markerPresent` comes from
 * `consumeAuthRedirectResult` (it reads and clears the marker, so this module
 * cannot read it itself — it is already gone by the time we are asked).
 */
export function detectRedirectReturn(markerPresent: boolean): RedirectReturnEvidence {
  const signals: RedirectReturnSignal[] = [];
  if (markerPresent) signals.push('marker');
  if (sdkRecordSignal()) signals.push('sdk-record');
  if (referrerSignal()) signals.push('referrer');
  return { isReturn: signals.length > 0, signals };
}
